import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { gamificationService } from '../../../lib/gamificationService';
import { supabase } from '../../../lib/supabase';

const AchievementSharing = ({ userId, gymId }) => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(null);

  useEffect(() => {
    loadAchievements();
  }, [userId]);

  const loadAchievements = async () => {
    setLoading(true);
    try {
      const { data } = await gamificationService?.getMemberAchievements(userId);
      setAchievements(data?.filter(a => a?.is_completed) || []);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShareAchievement = async (achievementId) => {
    setSharing(achievementId);
    try {
      const { error } = await supabase
        ?.from('member_achievements')
        ?.update({
          is_shared: true,
          shared_at: new Date()?.toISOString()
        })
        ?.eq('id', achievementId);

      if (!error) {
        alert('Achievement shared successfully!');
        loadAchievements();
      } else {
        alert('Failed to share achievement.');
      }
    } catch (error) {
      console.error('Error sharing achievement:', error);
      alert('An error occurred while sharing.');
    } finally {
      setSharing(null);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze':
        return 'bg-orange-100 text-orange-800';
      case 'silver':
        return 'bg-gray-100 text-gray-800';
      case 'gold':
        return 'bg-yellow-100 text-yellow-800';
      case 'platinum':
        return 'bg-blue-100 text-blue-800';
      case 'diamond':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'diamond':
        return 'Gem';
      case 'platinum': case'gold':
        return 'Crown';
      case 'silver':
        return 'Medal';
      default:
        return 'Award';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (achievements?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Icon name="Award" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Achievements Yet</h3>
        <p className="text-gray-600">Complete challenges and activities to earn achievements!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-purple-100 p-3 rounded-lg">
            <Icon name="Award" className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Your Achievements</h3>
            <p className="text-sm text-gray-600">Share your accomplishments with the community</p>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Share2" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-900">
              <p className="font-medium mb-1">Sharing Benefits:</p>
              <ul className="list-disc list-inside space-y-1 text-green-800">
                <li>Inspire other members with your progress</li>
                <li>Build community recognition and motivation</li>
                <li>Celebrate milestones with your gym family</li>
                <li>Earn bonus engagement points</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {achievements?.map((memberAchievement) => {
          const achievement = memberAchievement?.achievement;
          if (!achievement) return null;

          return (
            <div
              key={memberAchievement?.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
            >
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <Icon name={achievement?.icon_name || 'Award'} className="w-12 h-12" />
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTierColor(achievement?.tier)}`}>
                    <Icon name={getTierIcon(achievement?.tier)} className="w-3 h-3 inline mr-1" />
                    {achievement?.tier?.toUpperCase()}
                  </span>
                </div>
                <h3 className="text-xl font-bold">{achievement?.name}</h3>
              </div>

              <div className="p-6">
                <p className="text-gray-600 text-sm mb-4">{achievement?.description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Points Earned:</span>
                    <span className="font-semibold text-orange-600">
                      +{achievement?.points_value} points
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Completed:</span>
                    <span className="font-semibold text-gray-900">
                      {new Date(memberAchievement?.completed_at)?.toLocaleDateString()}
                    </span>
                  </div>
                  {memberAchievement?.is_shared && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <Icon name="Check" className="w-4 h-4" />
                      <span className="font-medium">Shared with community</span>
                    </div>
                  )}
                </div>

                {!memberAchievement?.is_shared ? (
                  <Button
                    onClick={() => handleShareAchievement(memberAchievement?.id)}
                    disabled={sharing === memberAchievement?.id}
                    className="w-full"
                  >
                    {sharing === memberAchievement?.id ? (
                      <>
                        <Icon name="Loader" className="w-4 h-4 animate-spin mr-2" />
                        Sharing...
                      </>
                    ) : (
                      <>
                        <Icon name="Share2" className="w-4 h-4 mr-2" />
                        Share Achievement
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <Icon name="Check" className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    <p className="text-sm font-medium text-green-800">Shared Successfully</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AchievementSharing;